<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { useAuthStore } from '@/entities/auth';
import { detectPwaPushNotificationSupport, formatPersonName, isPlatformHost, useIsTouchDevice } from '@/shared/lib';
import { BaseDropdown, type DropdownMenuItem } from '@/shared/ui/dropdown';
import { IconChevronDown, IconLogout, IconMegaphone } from '@/shared/ui/icons';

import { useSession } from '../model/use-session';

interface Props {
  collapsed?: boolean;
  extraItems?: DropdownMenuItem[];
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
  extraItems: () => [],
});

const authStore = useAuthStore();
const router = useRouter();
const { logout, isLoading } = useSession();
const { isTouchDevice } = useIsTouchDevice();
const pushNotificationSupport = detectPwaPushNotificationSupport();
const canManageNotifications = !isPlatformHost() && pushNotificationSupport !== 'unsupported';

const userName = computed(() => authStore.user?.first_name || 'Пользователь');

const userInitial = computed(() => userName.value.charAt(0).toUpperCase());

const isImpersonating = computed(() => authStore.isImpersonating);

const impersonationLabel = computed(() => {
  if (!isImpersonating.value) {
    return undefined;
  }

  const targetUser = authStore.impersonation.target_user;
  const formattedTargetName = targetUser ? formatPersonName(targetUser) : '';
  const targetName = formattedTargetName || targetUser?.phone || 'пользователем';
  const organizationTitle = authStore.impersonation.organization?.title;

  return organizationTitle ? `Вы работаете под ${targetName} · ${organizationTitle}` : `Вы работаете под ${targetName}`;
});

const menuItems = computed<DropdownMenuItem[]>(() => {
  const extraItems = props.extraItems;
  const notificationItems: DropdownMenuItem[] =
    isTouchDevice.value && canManageNotifications
      ? [
          {
            id: 'push-notifications',
            label: 'Уведомления',
            icon: IconMegaphone,
            onClick: async () => {
              await router.push({ name: 'push-notifications.index' });
            },
          },
          {
            id: 'account-divider',
            label: '',
            divider: true,
          },
        ]
      : [];

  return [
    ...notificationItems,
    ...extraItems,
    ...(extraItems.length > 0
      ? [
          {
            id: 'logout-divider',
            label: '',
            divider: true,
          },
        ]
      : []),
    {
      id: 'logout',
      label: isLoading.value ? 'Выход...' : 'Выйти',
      icon: IconLogout,
      loading: isLoading.value,
      disabled: isLoading.value,
      onClick: async () => {
        await logout();
      },
    },
  ];
});
</script>

<template>
  <BaseDropdown
    :items="menuItems"
    :content="{ align: 'start', side: 'top', sideOffset: 8 }"
    width="12rem"
  >
    <template #trigger="{ toggle }">
      <button
        class="user-menu-trigger"
        :class="{
          'user-menu-trigger--collapsed': props.collapsed,
          'user-menu-trigger--impersonating': isImpersonating,
        }"
        :title="impersonationLabel"
        :aria-label="impersonationLabel"
        @click="toggle"
      >
        <div class="user-menu-trigger__content">
          <span
            v-if="isImpersonating"
            class="user-menu-trigger__impersonation-marker"
            aria-hidden="true"
          />
          <span
            v-if="props.collapsed"
            key="initial"
            class="user-menu-trigger__text"
          >
            {{ userInitial }}
          </span>
          <span
            v-else
            key="full"
            class="user-menu-trigger__text"
          >
            {{ userName }}
          </span>
        </div>
        <IconChevronDown
          v-if="!props.collapsed"
          class="user-menu-trigger__chevron"
          :width="10"
          :height="10"
        />
      </button>
    </template>
  </BaseDropdown>
</template>

<style scoped>
.user-menu-trigger {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: var(--secondary-button-bg-color);
  color: var(--base-popup-text-color);
  border: 1px solid transparent;
  cursor: pointer;
  overflow: hidden;
  /* Плавный переход для всех свойств */
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    width var(--sidebar-transition-duration) ease,
    padding var(--sidebar-transition-duration) ease;
}

.user-menu-trigger:hover {
  background: var(--secondary-button-hover-bg-color);
}

.user-menu-trigger--impersonating,
.user-menu-trigger--impersonating:hover {
  border-color: var(--impersonation-accent);
  background: var(--impersonation-trigger-bg);
}

/* Collapsed состояние - уменьшенный padding */
.user-menu-trigger--collapsed {
  padding: 0.5rem;
  justify-content: center;
}

.user-menu-trigger__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 1rem;
}

.user-menu-trigger__impersonation-marker {
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 999px;
  background: var(--impersonation-accent);
}

.user-menu-trigger--collapsed .user-menu-trigger__impersonation-marker {
  position: absolute;
  top: 4px;
  right: 4px;
}

.user-menu-trigger__text {
  display: inline-block;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
}

.user-menu-trigger__chevron {
  flex-shrink: 0;
}

/* Transition анимация для смены текста */
.user-text-enter-active,
.user-text-leave-active {
  transition:
    opacity var(--sidebar-transition-duration) ease,
    transform var(--sidebar-transition-duration) ease;
}

.user-text-enter-from {
  opacity: 0;
  transform: scale(0.9);
}

.user-text-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.user-text-enter-to,
.user-text-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* Плавная анимация для chevron */
.chevron-fade-enter-active,
.chevron-fade-leave-active {
  transition:
    opacity var(--sidebar-transition-duration) ease,
    transform var(--sidebar-transition-duration) ease;
}

.chevron-fade-enter-from {
  opacity: 0;
  transform: translateX(-4px);
}

.chevron-fade-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}

.chevron-fade-enter-to,
.chevron-fade-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
